import React from 'react';
import { formatCurrency } from '../utils/formatters';
import { Wallet, Calendar, Clock, Store, X, User } from 'lucide-react';

function TransactionDetailDialog({ transaction, onClose, onEdit, onDelete, categories, wallets = [] }) {
  if (!transaction) return null;
  
  const cat = (categories.expense || []).concat(categories.income || []).find(c => c.name === transaction.category);
  const wallet = wallets.find(w => w.id === transaction.walletId);
  const personName = transaction.debtPersonName || transaction.personName;
  const showDebtInfo = Boolean(personName);
  
  // Determina se è spesa o entrata (o debito/credito)
  const isExpense = transaction._type === 'expense' || transaction._type === 'debt' || (transaction._type == null && (categories.expense || []).some(c => c.name === transaction.category));
  const isIncome = transaction._type === 'income' || transaction._type === 'credit' || (transaction._type == null && (categories.income || []).some(c => c.name === transaction.category));
  const isDebt = transaction._type === 'debt';
  const isCredit = transaction._type === 'credit';
  
  // Formatta la data e ora
  const transactionDate = transaction.date ? new Date(transaction.date) : new Date();
  const formattedDate = transactionDate.toLocaleDateString('it-IT', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const formattedTime = transactionDate.toLocaleTimeString('it-IT', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999]" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-[32px] shadow-2xl w-full max-w-4xl transform transition-all duration-300 border border-gray-200 dark:border-gray-700 max-h-[78vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-blue-600/90 backdrop-blur-sm text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-t-[32px]">
          <div className="flex items-center justify-between gap-3">
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-center flex-1">Dettagli Transazione</h2>
            <div className="w-5 h-5" aria-hidden="true" />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-5">
          {/* Importo principale */}
          <div className="space-y-3">
            <div className="text-center">
              <div className={`text-3xl sm:text-4xl font-semibold 
                ${(isDebt || isCredit) ? 'text-sky-600' : 
                  isExpense ? 'text-red-600' : 'text-green-600'}`}>
                {(isExpense || isCredit) ? '-' : (isIncome || isDebt) ? '+' : ''}{formatCurrency(Math.abs(transaction.amount))}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {isDebt ? 'Debito' : isCredit ? 'Credito' : isExpense ? 'Spesa' : isIncome ? 'Entrata' : 'Transazione'}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-gray-600 dark:text-gray-400 flex-wrap">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100/80 dark:bg-gray-700/70">
                <Calendar className="w-3.5 h-3.5" aria-hidden="true" />
                <span>{formattedDate}</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100/80 dark:bg-gray-700/70">
                <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                <span>{formattedTime}</span>
              </div>
            </div>
          </div>

          {/* Dettagli principali */}
          <div className="space-y-3">
            <div className={`grid gap-3 ${wallet ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/70 border border-gray-100 dark:border-gray-700 shadow-sm">
                <span className="text-3xl" aria-hidden="true">{cat?.icon || '📦'}</span>
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Categoria</div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{transaction.category}</div>
                </div>
              </div>

              {wallet && (
                <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/70 border border-gray-100 dark:border-gray-700 shadow-sm">
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1.5">
                    <Wallet className="w-4 h-4 text-gray-500 dark:text-gray-300" aria-hidden="true" />
                    <span>Conto</span>
                  </div>
                  <div className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0 border border-white/60"
                      style={{ backgroundColor: wallet.color }}
                      aria-hidden="true"
                    />
                    <span className="truncate">{wallet.name}</span>
                  </div>
                </div>
              )}
            </div>

            {(transaction.store || personName) && (
              <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/70 border border-gray-100 dark:border-gray-700 shadow-sm">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1.5">
                  <Store className="w-4 h-4" aria-hidden="true" />
                  <span>{personName ? 'Persona' : 'Negozio'}</span>
                </div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 break-words">{personName || transaction.store}</div>
              </div>
            )}

            {showDebtInfo && (
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/70 border border-gray-100 dark:border-gray-700 shadow-sm">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-2xl flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {transaction.debtType === 'credit' ? 'Credito' : 'Debito'}
                  </div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {personName}
                  </div>
                </div>
              </div>
            )}

            {transaction.note && (
              <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/70 border border-gray-100 dark:border-gray-700 shadow-sm">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Note</div>
                <div className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">{transaction.note}</div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onEdit} className="btn btn-secondary flex-1">
            Modifica
          </button>
          <button onClick={onDelete} className="btn btn-danger flex-1">
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

export default TransactionDetailDialog; 
